import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { fetchLocations } from "../../services/auth/ubtService";
import type { SortByType } from "../../services/auth/ubtService";
import { DropdownOption } from "../../components/common/Dropdown";
import {
  transformToDropdownOptions,
  transformCostCenterOptions,
  addDefaultOption,
} from "../../utils/dropdownTransformers";
import type { APIResponse } from "../../types/selectUBT.types";
import {
  groupProjectsByTeam,
  type GroupedProject,
  type ProjectItem,
} from "../../utils/projectDataTransformers";

export const searchByOptions = [
  { id: 0, label: "UBT Name", value: "UBTName" },
  { id: 1, label: "Cost Center Name", value: "CostCenterName" },
  { id: 2, label: "Cost Center Number", value: "CostCenterNumber" },
  { id: 3, label: "UBT ID#", value: "UBTId" },
];
// Default dropdown options
const defaultOption: DropdownOption = {
  id: 0,
  label: "All",
  value: "All",
};

const rating = [
  { id: 0, label: "Acurate and timely in and out", value: "all" },
];

const costCenters = [
  { id: 0, label: "Med/Surge-General", value: "medSurgeGeneral" },
];

const valueCompass = [{ id: 0, label: "Best Quality", value: "bestQuality" }];

const ratingOption = [
  {
    id: 0,
    label: "Accurate and timely clocking in and out",
    value: "accurateClocking",
  },
];

const results = [
  { id: 0, label: "Scretch Met", value: "" },
  {
    id: 2,
    label: "Target Met -- Spread[Spread Practice to Other Teams]",
    value: "",
  },
];

const testOfChangeRating = [
  { id: 0, label: "Unrated", value: "" },
  { id: 2, label: "Abandon]", value: "" },
];

const searchingClosedAndOpenProjects = [{ id: 0, label: "Open", value: "" }];

/** Typed shape of the search-projects API response handled in `fetchProjectList.fulfilled`. */
interface ProjectListPayload {
  items?: ProjectItem[];
  meta?: {
    total?: number;
    totalPages?: number;
    pageNumber?: number;
    pageSize?: number;
  };
}

interface SelectProjectState {
  /** Project list stored as team-grouped entries. Populated by `fetchProjectList`. */
  projectList: GroupedProject[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  // Dropdown options state
  searchByOptions: any[];
  rating: any[]; // Will be replaced by focusAreas from new API
  ratingOption: any[];
  valueCompass: any[]; // Will be replaced by valueCompassStatic from new API
  testOfChangeRating: any[]; // Will be replaced by testOfChangeRatings from new API
  costCenters: any[];
  searchingClosedAndOpenedProjects: any[];
  results: any[]; // Will be replaced by results from new API
  projectRegion: any[];
  projectEntity: any[];
  projectArea: any[];
  projectCenter: any[];
  projectLocation: any[];
  // New API data
  focusAreas: any[]; // From search-project API with operation=getfocusarea
  metricsData: any[]; // From search-project API with operation=getmetriclist
  // Selected values tracking
  selectedRegionCode: DropdownOption;
  selectedEntityCode: DropdownOption;
  selectedAreaCode: DropdownOption;
  selectedCenterCode: DropdownOption;
  selectedLocationCode: DropdownOption;
  selectedCostCenterCode: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  sortByCostCenter: string;
  // Separate selected values for each MultiCheckboxSelect
  selectedRating: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedValueCompass: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedMetrix: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedResults: { isAllSelected: boolean; selectedIds: (string | number)[] };
  selectedTestOfChangeRating: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  selectedProjectType: string;
  selectedSearchBy: DropdownOption;
  selectedSearchByText: string;
  selectedFromId: string;
  selectedToId: string;
  selectedProjectName: string;
  // Loading states for dropdowns
  loadingSearchBy: boolean;
  loadingDropdowns: boolean;
}

const initialState: SelectProjectState = {
  // GroupedProject[] — populated on first fetchProjectList response.
  projectList: [],
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 20,
  totalItems: 0,
  totalPages: 0,
  // Dropdown options - start with Northern California as default, will be updated by API calls
  searchByOptions: [],
  rating: [],
  ratingOption: [],
  valueCompass: [],
  costCenters: [],
  testOfChangeRating: [],
  searchingClosedAndOpenedProjects: [],
  results: [],
  projectRegion: [{ id: "02", label: "Northern California", value: "02" }],
  projectEntity: [],
  projectArea: [],
  projectCenter: [],
  projectLocation: [],
  // New API data
  focusAreas: [],
  metricsData: [],
  // Selected values - initialize with hard-coded defaults matching API structure
  selectedRegionCode: { id: "02", label: "Northern California", value: "02" },
  selectedEntityCode: { id: 0, label: "All", value: "All" },
  selectedAreaCode: { id: 0, label: "All", value: "All" },
  selectedCenterCode: { id: 0, label: "All", value: "All" },
  selectedLocationCode: { id: 0, label: "All", value: "All" },
  selectedCostCenterCode: { isAllSelected: true, selectedIds: [] },
  sortByCostCenter: "id",
  // Separate selected values for each MultiCheckboxSelect - default to all selected
  selectedRating: { isAllSelected: true, selectedIds: [] },
  selectedValueCompass: { isAllSelected: true, selectedIds: [] },
  selectedMetrix: { isAllSelected: true, selectedIds: [] },
  selectedResults: { isAllSelected: true, selectedIds: [] },
  selectedTestOfChangeRating: { isAllSelected: true, selectedIds: [] },
  selectedProjectType: "Active",
  selectedSearchBy: { id: 0, label: "UBT Name", value: "UBTName" },
  selectedSearchByText: "",
  selectedFromId: "",
  selectedToId: "",
  selectedProjectName: "",
  // Loading states
  loadingSearchBy: false,
  loadingDropdowns: false,
};

// Helper function to build request parameters
const buildProjectListParams = (
  page: number,
  perPage: number,
  filters: Record<string, any>,
): Record<string, any> => {
  const params: Record<string, any> = {
    page,
    per_page: perPage,
  };

  const paramMappings: Record<string, string> = {
    searchBy: "searchBy",
    searchString: "searchString",
    fromId: "fromId",
    toId: "toId",
    searchByProject: "searchByProject",
    region: "region",
    entity: "entity",
    area: "area",
    center: "center",
    location: "location",
    costCenter: "costCenter",
    focusArea: "focusArea",
    metric: "metric",
    valueCompassDesc: "valueCompass",
    result: "result",
    sortBy: "sortBy",
    sortOrder: "sortOrder",
    sortColumn: "sortColumn",
    projectType: "projectType",
    rating: "rating",
    projectName: "projectName",
    startDate: "startDate",
    endDate: "endDate",
    minROI: "minRoi",
    maxROI: "maxRoi",
  };

  Object.entries(paramMappings).forEach(([key, paramKey]) => {
    if (filters[key]) {
      params[paramKey] = filters[key];
    }
  });

  if (filters.costCenter) {
    params.costCenter = filters.costCenter;
  }

  return params;
};

// Helper function to extract error message
const extractErrorMessage = (error: any): string => {
  if (
    error?.message === "canceled" ||
    error?.message === "Request cancelled" ||
    error?.code === "ECONNABORTED"
  ) {
    return "Request cancelled";
  }

  return (
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.statusText ??
    error?.message ??
    "Failed to fetch manager applications"
  );
};

export const fetchProjectList = createAsyncThunk(
  "ubtPortal/fetchProjectList",
  async (
    {
      page,
      perPage,
      searchBy,
      searchString,
      fromId,
      toId,
      searchByProject,
      searchText,
      region,
      entity,
      area,
      center,
      location,
      costCenter,
      focusArea,
      metric,
      valueCompassDesc,
      result,
      sortBy,
      sortOrder,
      sortColumn,
      projectType,
      rating,
      projectName,
      startDate,
      endDate,
      minROI,
      maxROI,
    }: {
      page: number;
      perPage: number;
      searchBy?: string | number;
      searchString?: string;
      fromId?: string;
      toId?: string;
      searchByProject?: string;
      searchText?: string;
      region?: string;
      entity?: string;
      area?: string;
      center?: string;
      location?: string;
      costCenter?: string;
      focusArea?: string;
      metric?: string;
      valueCompassDesc?: string;
      result?: string;
      sortBy?: string;
      sortOrder?: string;
      sortColumn?: string;
      projectType?: string;
      rating?: string;
      projectName?: string;
      startDate?: string;
      endDate?: string;
      minROI?: string;
      maxROI?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const params = buildProjectListParams(page, perPage, {
        searchBy,
        searchString,
        fromId,
        toId,
        searchByProject,
        searchText,
        region,
        entity,
        area,
        center,
        location,
        costCenter,
        focusArea,
        metric,
        valueCompassDesc,
        result,
        sortBy,
        sortOrder,
        sortColumn,
        projectType,
        rating,
        projectName,
        startDate,
        endDate,
        minROI,
        maxROI,
      });

      const options: any = {
        signal,
        params,
      };

      const response = await api.get(
        endpoints.ubt.project.fetchProjectList,
        options,
      );

      return response.data;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch Search By options
export const fetchSearchByOptions = createAsyncThunk(
  "selectUBT/fetchSearchByOptions",
  async (_, { rejectWithValue }) => {
    try {
      return searchByOptions;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch search by options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchRating = createAsyncThunk(
  "selectUBT/fetchRating",
  async (_, { rejectWithValue }) => {
    try {
      return rating;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch rating options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchRatingOption = createAsyncThunk(
  "selectUBT/fetchRatingOptions",
  async (_, { rejectWithValue }) => {
    try {
      return ratingOption;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch rating option options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchCostCenters = createAsyncThunk(
  "selectUBT/fetchCostCenters",
  async (_, { rejectWithValue }) => {
    try {
      return costCenters;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch cost centers options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchValueCompass = createAsyncThunk(
  "selectUBT/fetchValueCompass",
  async (_, { rejectWithValue }) => {
    try {
      return valueCompass;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch value compass options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchResults = createAsyncThunk(
  "selectUBT/fetchResults",
  async (_, { rejectWithValue }) => {
    try {
      return results;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch results";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchtestOfChangeRating = createAsyncThunk(
  "selectUBT/fetchTestOfChangeRating",
  async (_, { rejectWithValue }) => {
    try {
      return testOfChangeRating;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch fetchTestOfChangeRating options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchSearchingClosedAndOpenedProjects = createAsyncThunk(
  "selectUBT/fetchSearchingClosedAndOpenedProjects",
  async (_, { rejectWithValue }) => {
    try {
      return searchingClosedAndOpenProjects;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch searchingClosedAndOpenedProjects options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchProjectRegion = createAsyncThunk(
  "project/fetchProjectRegion",
  async (_, { rejectWithValue }) => {
    try {
      const response: APIResponse = await fetchLocations({
        loadValue: "REGION",
      });

      const regionOptions = transformToDropdownOptions(
        response?.selectedList ?? [],
        false,
      );
      return regionOptions;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch project region options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchProjectArea = createAsyncThunk(
  "project/fetchProjectArea",
  async (
    {
      regionCode,
      sortBy = "id",
      isRestored = false,
    }: { regionCode: string; sortBy?: string; isRestored?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const sortByValue: SortByType = sortBy === "name" ? "DEPTNAME" : "DEPTID";
      const apiParams = {
        loadValue: "AREA" as const,
        regionCode,
        sortBy: sortByValue,
      };
      const response: APIResponse = await fetchLocations(apiParams);

      const areaOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      const entityOptions = addDefaultOption(
        transformToDropdownOptions(response?.entityList ?? []),
        defaultOption,
      );

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { areaOptions, entityOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch area options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchProjectCenter = createAsyncThunk(
  "project/fetchProjectCenter",
  async (
    {
      regionCode,
      areaCode,
      sortBy = "id",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const sortByValue: SortByType = sortBy === "name" ? "DEPTNAME" : "DEPTID";
      const response: APIResponse = await fetchLocations({
        loadValue: "CENTER",
        regionCode,
        areaCode,
        sortBy: sortByValue,
      });

      const centerOptions = addDefaultOption(
        transformToDropdownOptions(response?.selectedList ?? []),
        defaultOption,
      );

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { centerOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch center options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchProjectLocation = createAsyncThunk(
  "project/fetchProjectLocation",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      sortBy = "id",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const sortByValue: SortByType = sortBy === "name" ? "DEPTNAME" : "DEPTID";
      const response: APIResponse = await fetchLocations({
        loadValue: "LOCATION",
        regionCode,
        areaCode,
        centerCode,
        sortBy: sortByValue,
      });

      // Only return locations if center is not 'All'
      let locationOptions: DropdownOption[] = [];
      if (centerCode !== "All") {
        locationOptions = addDefaultOption(
          transformToDropdownOptions(response?.selectedList ?? []),
          defaultOption,
        );
      }

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { locationOptions, costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch location options";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchProjectCostCenters = createAsyncThunk(
  "project/fetchProjectCostCenters",
  async (
    {
      regionCode,
      areaCode,
      centerCode,
      locationCode,
      sortBy = "id",
      isRestored = false,
    }: {
      regionCode: string;
      areaCode: string;
      centerCode: string;
      locationCode?: string;
      sortBy?: string;
      isRestored?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const sortByValue: SortByType = sortBy === "name" ? "DEPTNAME" : "DEPTID";
      const response: APIResponse = await fetchLocations({
        loadValue: "COST_CENTER",
        regionCode,
        areaCode,
        centerCode,
        locationCode: locationCode ?? "ALL",
        sortBy: sortByValue,
      });

      const costCenterOptions = transformCostCenterOptions(
        response?.costCenterList ?? [],
      );

      return { costCenterOptions, isRestored };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch cost center options";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch search project focus data (operation=getfocusarea)
export const fetchSearchProjectFocusData = createAsyncThunk(
  "project/fetchSearchProjectFocusData",
  async (regionCode: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.ubt.project.searchProject(regionCode),
        {
          params: { operation: "getfocusarea" },
        },
      );

      const data = response.data;

      // Transform focusAreas to dropdown format
      const focusAreasOptions = (data.focusAreas ?? []).map((item: any) => ({
        id: item.focusId,
        label: item.focusAreaDesc,
        value: item.focusId,
      }));

      // Transform valueCompassStatic to dropdown format
      const valueCompassOptions = (data.valueCompassStatic ?? []).map(
        (item: any) => ({
          id: item.vcId,
          label: item.vcDesc,
          value: item.vcId,
        }),
      );

      // Transform results to dropdown format
      const resultsOptions = (data.results ?? []).map((item: any) => ({
        id: item.resultId,
        label: item.resultDesc,
        value: item.resultCode,
      }));

      // Transform testOfChangeRatings to dropdown format
      const testOfChangeRatingsOptions = (data.testOfChangeRatings ?? []).map(
        (item: any) => ({
          id: item.RATING_ID,
          label: item.RATING,
          value: item.RATING_ID,
        }),
      );

      return {
        focusAreas: data.focusAreas ?? [],
        focusAreasOptions,
        valueCompassOptions,
        resultsOptions,
        testOfChangeRatingsOptions,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch search project focus data";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch search project metrics data (operation=getmetriclist)
export const fetchSearchProjectMetrics = createAsyncThunk(
  "project/fetchSearchProjectMetrics",
  async (
    { regionCode, focusIds }: { regionCode: string; focusIds?: string },
    { rejectWithValue },
  ) => {
    try {
      const params: any = { operation: "getmetriclist" };
      if (focusIds) {
        params.focusId = focusIds;
      }

      const response = await api.get(
        endpoints.ubt.project.searchProject(regionCode),
        {
          params,
        },
      );

      const data = response.data;

      // Transform metrics to dropdown format
      const metricsOptions = (data.metrics ?? []).map((item: any) => ({
        id: item.metricId,
        label: item.metric,
        value: item.metricCode,
      }));

      return {
        metrics: data.metrics ?? [],
        metricsOptions,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch search project metrics";
      return rejectWithValue(errorMessage);
    }
  },
);

const searchProjectSlice = createSlice({
  name: "searchProject",
  initialState,
  reducers: {
    resetSearchProjectState: (state) => {
      state.projectList = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSelectedRegionCode: (state, action) => {
      state.selectedRegionCode = action.payload;
    },
    setSelectedEntityCode: (state, action) => {
      state.selectedEntityCode = action.payload;
    },
    setSelectedAreaCode: (state, action) => {
      state.selectedAreaCode = action.payload;
    },
    setSelectedCenterCode: (state, action) => {
      state.selectedCenterCode = action.payload;
    },
    setSelectedLocationCode: (state, action) => {
      state.selectedLocationCode = action.payload;
    },
    setSelectedCostCenterCode: (state, action) => {
      state.selectedCostCenterCode = action.payload;
    },
    setSortByCostCenter: (state, action) => {
      state.sortByCostCenter = action.payload;
    },
    setSelectedRating: (state, action) => {
      state.selectedRating = action.payload;
    },
    setSelectedValueCompass: (state, action) => {
      state.selectedValueCompass = action.payload;
    },
    setSelectedMetrix: (state, action) => {
      state.selectedMetrix = action.payload;
    },
    setSelectedResults: (state, action) => {
      state.selectedResults = action.payload;
    },
    setSelectedTestOfChangeRating: (state, action) => {
      state.selectedTestOfChangeRating = action.payload;
    },
    setSelectedProjectType: (state, action) => {
      state.selectedProjectType = action.payload;
    },
    setSelectedSearchBy: (state, action) => {
      state.selectedSearchBy = action.payload;
    },
    setSelectedSearchByText: (state, action) => {
      state.selectedSearchByText = action.payload;
    },
    setSelectedFromId: (state, action) => {
      state.selectedFromId = action.payload;
    },
    setSelectedToId: (state, action) => {
      state.selectedToId = action.payload;
    },
    setSelectedProjectName: (state, action) => {
      state.selectedProjectName = action.payload;
    },
    resetDependentDropdowns: (state, action) => {
      const level = action.payload.level;
      if (level === "region") {
        state.projectEntity = [];
        state.projectArea = [];
        state.projectCenter = [];
        state.projectLocation = [];
        state.costCenters = [];
        state.selectedEntityCode = defaultOption;
        state.selectedAreaCode = defaultOption;
        state.selectedCenterCode = defaultOption;
        state.selectedLocationCode = defaultOption;
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      } else if (level === "area") {
        state.projectCenter = [];
        state.projectLocation = [];
        state.costCenters = [];
        state.selectedCenterCode = defaultOption;
        state.selectedLocationCode = defaultOption;
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      } else if (level === "center") {
        state.projectLocation = [];
        state.costCenters = [];
        state.selectedLocationCode = defaultOption;
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      } else if (level === "location") {
        state.costCenters = [];
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    },
  },
  extraReducers: (builder) => {
    // ── Project List ─────────────────────────────────────────────────────────
    // On success, `projectList` is kept for legacy consumers while
    // `groupedProjectList` provides the team-grouped view used by the
    // Search Projects results table.
    builder.addCase(fetchProjectList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProjectList.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload as ProjectListPayload | null;
      // Handle API response with items array
      if (payload?.items && Array.isArray(payload.items)) {
        // Group items by team and store as GroupedProject[].
        state.projectList = groupProjectsByTeam(payload.items);
        // Populate pagination meta when the API supplies it.
        if (payload.meta) {
          state.totalItems = payload.meta.total ?? payload.items.length;
          state.totalPages = payload.meta.totalPages ?? 1;
          state.currentPage = payload.meta.pageNumber ?? 1;
          state.perPage = payload.meta.pageSize ?? 10;
        } else {
          state.totalItems = payload.items.length;
        }
      } else {
        // Graceful fallback: wrap a bare object in an array then group, or clear.
        state.projectList = payload
          ? groupProjectsByTeam([payload as unknown as ProjectItem])
          : [];
      }
    });
    builder.addCase(fetchProjectList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Search By Options
    builder.addCase(fetchSearchByOptions.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchSearchByOptions.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.searchByOptions = action.payload ?? [];
    });
    builder.addCase(fetchSearchByOptions.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchRating.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchRating.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.rating = action.payload ?? [];
    });
    builder.addCase(fetchRating.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchRatingOption.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchRatingOption.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.ratingOption = action.payload ?? [];
    });
    builder.addCase(fetchRatingOption.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchSearchingClosedAndOpenedProjects.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(
      fetchSearchingClosedAndOpenedProjects.fulfilled,
      (state, action) => {
        state.loadingSearchBy = false;
        state.searchingClosedAndOpenedProjects = action.payload ?? [];
      },
    );
    builder.addCase(fetchSearchingClosedAndOpenedProjects.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchValueCompass.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchValueCompass.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.valueCompass = action.payload ?? [];
    });
    builder.addCase(fetchValueCompass.rejected, (state) => {
      state.loadingSearchBy = false;
    });
    builder.addCase(fetchResults.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchResults.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.results = action.payload ?? [];
    });
    builder.addCase(fetchResults.rejected, (state) => {
      state.loadingSearchBy = false;
    });
    builder.addCase(fetchtestOfChangeRating.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchtestOfChangeRating.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.testOfChangeRating = action.payload ?? [];
    });
    builder.addCase(fetchtestOfChangeRating.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchCostCenters.pending, (state) => {
      state.loadingSearchBy = true;
    });
    builder.addCase(fetchCostCenters.fulfilled, (state, action) => {
      state.loadingSearchBy = false;
      state.costCenters = action.payload ?? [];
    });
    builder.addCase(fetchCostCenters.rejected, (state) => {
      state.loadingSearchBy = false;
    });

    builder.addCase(fetchProjectRegion.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchProjectRegion.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.projectRegion = action.payload ?? [];
    });
    builder.addCase(fetchProjectRegion.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    builder.addCase(fetchProjectArea.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchProjectArea.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.projectArea = action.payload.areaOptions ?? [];
      state.projectEntity = action.payload.entityOptions ?? [];
      state.costCenters = action.payload.costCenterOptions ?? [];
      // Reset cost center selection if not restoring from session
      if (!action.payload.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    });
    builder.addCase(fetchProjectArea.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    builder.addCase(fetchProjectCenter.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchProjectCenter.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.projectCenter = action.payload.centerOptions ?? [];
      state.costCenters = action.payload.costCenterOptions ?? [];
      if (!action.payload.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    });
    builder.addCase(fetchProjectCenter.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    builder.addCase(fetchProjectLocation.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchProjectLocation.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.projectLocation = action.payload.locationOptions ?? [];
      state.costCenters = action.payload.costCenterOptions ?? [];
      if (!action.payload.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    });
    builder.addCase(fetchProjectLocation.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    builder.addCase(fetchProjectCostCenters.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchProjectCostCenters.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.costCenters = action.payload.costCenterOptions ?? [];
      if (!action.payload.isRestored) {
        state.selectedCostCenterCode = { isAllSelected: true, selectedIds: [] };
      }
    });
    builder.addCase(fetchProjectCostCenters.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Search Project Focus Data
    builder.addCase(fetchSearchProjectFocusData.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchSearchProjectFocusData.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.focusAreas = action.payload.focusAreas;
      // Update the dropdown options with API data
      state.rating = action.payload.focusAreasOptions ?? [];
      state.valueCompass = action.payload.valueCompassOptions ?? [];

      // Filter out "All" from results and testOfChangeRating
      const resultsFiltered = (action.payload.resultsOptions ?? []).filter(
        (option: any) => option.value !== "All" && option.label !== "All",
      );
      const testOfChangeRatingFiltered = (
        action.payload.testOfChangeRatingsOptions ?? []
      ).filter(
        (option: any) => option.value !== "All" && option.label !== "All",
      );

      state.results = resultsFiltered;
      state.testOfChangeRating = testOfChangeRatingFiltered;
    });
    builder.addCase(fetchSearchProjectFocusData.rejected, (state) => {
      state.loadingDropdowns = false;
    });

    // Search Project Metrics
    builder.addCase(fetchSearchProjectMetrics.pending, (state) => {
      state.loadingDropdowns = true;
    });
    builder.addCase(fetchSearchProjectMetrics.fulfilled, (state, action) => {
      state.loadingDropdowns = false;
      state.metricsData = action.payload.metrics;
      state.ratingOption = action.payload.metricsOptions ?? [];
    });
    builder.addCase(fetchSearchProjectMetrics.rejected, (state) => {
      state.loadingDropdowns = false;
    });
  },
});

export const {
  resetSearchProjectState,
  setCurrentPage,
  setSelectedRegionCode,
  setSelectedEntityCode,
  setSelectedAreaCode,
  setSelectedCenterCode,
  setSelectedLocationCode,
  setSelectedCostCenterCode,
  setSortByCostCenter,
  setSelectedRating,
  setSelectedValueCompass,
  setSelectedMetrix,
  setSelectedResults,
  setSelectedTestOfChangeRating,
  setSelectedProjectType,
  setSelectedSearchBy,
  setSelectedSearchByText,
  setSelectedFromId,
  setSelectedToId,
  setSelectedProjectName,
  resetDependentDropdowns,
} = searchProjectSlice.actions;
export default searchProjectSlice.reducer;
